import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, DollarSign, Calendar, Loader2, Store, RefreshCw, XCircle, TrendingUp } from "lucide-react";
import api from "@/lib/api";

/**
 * v2.7.58+E35 · DELIVERY SYSTEM · Section 4
 *
 * Past Trip History Vault. Rendered inside the Driver Profile page under
 * the new "Deliveries" tab. Shows every completed / cancelled manifest
 * assigned to the calling driver, plus lifetime totals for the compliance
 * retention view. Section 5 will augment this with the payout ledger once
 * the auto-completion pipeline goes live.
 */
export default function DeliveryHistoryVault() {
  const [items, setItems]   = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoad]  = useState(true);

  const refresh = useCallback(async () => {
    try {
      setLoad(true);
      const { data } = await api.get("/deliveries/mine/history", { params: { limit: 100 } });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotals(data?.totals || null);
    } catch {
      /* profile-tab friendly failure — empty state below covers it */
    } finally { setLoad(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-4" data-testid="delivery-history-vault">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-zinc-100 flex items-center gap-2">
            <Package className="h-4 w-4 text-emerald-300"/>Delivery History
          </h3>
          <p className="text-xs text-zinc-500">Every completed multi-stop manifest you&rsquo;ve closed out.</p>
        </div>
        <Button
          onClick={refresh}
          variant="outline"
          size="sm"
          className="bg-zinc-950 border-zinc-700 text-xs"
          data-testid="delivery-history-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1"/>Refresh
        </Button>
      </div>

      {/* Totals banner */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="delivery-history-totals">
          <TotalPill
            icon={Package}
            label="Deliveries"
            value={totals.count}
            accent="text-zinc-100"
          />
          <TotalPill
            icon={TrendingUp}
            label="Completed"
            value={totals.delivered}
            accent="text-emerald-300"
          />
          <TotalPill
            icon={DollarSign}
            label="Gross"
            value={`$${(totals.total_gross_earned || 0).toFixed(2)}`}
            accent="text-amber-300"
          />
          <TotalPill
            icon={DollarSign}
            label="Tips"
            value={`$${(totals.total_tips || 0).toFixed(2)}`}
            accent="text-emerald-300"
          />
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-8 text-zinc-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2"/>Loading vault…
        </div>
      )}

      {!loading && items.length === 0 && (
        <Card className="p-8 bg-zinc-950 border-zinc-800 text-center" data-testid="delivery-history-empty">
          <Package className="h-8 w-8 mx-auto text-zinc-700 mb-2"/>
          <div className="text-sm text-zinc-400">No completed deliveries yet.</div>
          <div className="text-[11px] text-zinc-500 mt-1">Once dispatch assigns you a multi-stop manifest and you close it out, it will appear here.</div>
        </Card>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((d) => (
            <HistoryRow key={d.id} d={d}/>
          ))}
        </div>
      )}
    </div>
  );
}

function TotalPill({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-3 bg-zinc-900 border-zinc-800">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
        <Icon className="h-3 w-3"/>{label}
      </div>
      <div className={`text-lg font-extrabold mt-0.5 ${accent}`}>{value}</div>
    </Card>
  );
}

function HistoryRow({ d }) {
  const delivered = d.status === "DELIVERED";
  const stopCount = d.stop_count || (d.stops || []).length;
  const total     = typeof d.quote_total === "number" ? d.quote_total : null;
  const tip       = typeof d.quote_tip_amount === "number" ? d.quote_tip_amount : null;
  const closedAt  = d.delivered_at || d.updated_at || d.created_at;
  return (
    <Card
      className={delivered
        ? "p-3 bg-zinc-900 border-zinc-800 hover:border-emerald-400/30 transition-colors"
        : "p-3 bg-zinc-950 border-zinc-800 opacity-70"}
      data-testid={`delivery-history-row-${d.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
          delivered ? "bg-emerald-400/15 text-emerald-300" : "bg-rose-400/15 text-rose-300"
        }`}>
          {delivered ? <Package className="h-4 w-4"/> : <XCircle className="h-4 w-4"/>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded ${
              delivered ? "bg-emerald-400 text-zinc-950" : "bg-rose-500/30 text-rose-200"
            }`}>{d.status}</span>
            <span className="text-[10px] text-zinc-500 font-mono">#{d.id.slice(0, 8)}</span>
            <span className="text-[10px] text-zinc-500 flex items-center gap-1">
              <Calendar className="h-3 w-3"/>
              {closedAt ? new Date(closedAt).toLocaleString() : "—"}
            </span>
          </div>
          <div className="mt-1 text-sm font-bold text-zinc-100 truncate">
            {d.customer_name} · {stopCount} stop{stopCount === 1 ? "" : "s"}
          </div>
          <div className="mt-0.5 text-[11px] text-zinc-500 flex items-center gap-1 truncate">
            <Store className="h-3 w-3 flex-shrink-0"/>
            {(d.stops || []).map(s => s.business_name).filter(Boolean).join(" → ") || "—"}
          </div>
          {(total !== null || tip !== null) && (
            <div className="mt-1 flex items-center gap-3 text-[11px]">
              {total !== null && (
                <span className="text-zinc-300">
                  Gross · <span className="font-bold text-amber-300">${total.toFixed(2)}</span>
                </span>
              )}
              {tip !== null && tip > 0 && (
                <span className="text-zinc-300">
                  Tip · <span className="font-bold text-emerald-300">${tip.toFixed(2)}</span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
